import { type QueryClient, hashKey, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { type UnifiedTransfer } from '@column/api';
import { Currencies } from '@column/column-ui-kit';

import { useSessionStore } from '~/stores/Session';

interface CachedTransferList {
  has_more: boolean;
  total_results_count?: number;
  transfers: UnifiedTransfer[];
}

export interface OptimisticTransferInput {
  id: string;
  type: string;
  amount?: number;
  currencyCode?: string;
  description?: string;
  status?: string;
  createdAt?: Date | string;
  isIncoming?: boolean;
  senderInternalAccount?: { bankAccountId?: string; accountNumberId?: string };
  receiverInternalAccount?: { bankAccountId?: string; accountNumberId?: string };
  checkNumber?: string;
  transferBatchId?: string;
  pendingApproval?: boolean;
}

export interface OptimisticSessionContext {
  platformId?: string;
  liveMode?: boolean;
}

export interface OptimisticTransferUpdate {
  id: string;
  status: string;
}

const transferTypeMatches = (filter: string, transferType: string): boolean => {
  const filters = filter.split(',').map((f) => f.trim());
  return filters.some((f) => {
    if (f === 'achCredit') return transferType === 'ach_credit';
    if (f === 'achDebit') return transferType === 'ach_debit';
    if (f === 'check') return transferType === 'check_credit' || transferType === 'check_debit';
    return f === transferType;
  });
};

const toUnifiedTransfer = (input: OptimisticTransferInput): UnifiedTransfer => {
  const createdAt =
    input.createdAt instanceof Date ? input.createdAt.toISOString() : (input.createdAt ?? new Date().toISOString());
  const status = input.status ? input.status.toLowerCase() : '';
  return {
    id: input.id,
    type: input.type,
    amount: input.amount ?? 0,
    currency_code: input.currencyCode ?? 'USD',
    description: input.description ?? '',
    status,
    ...(input.pendingApproval ? { pending_approval: true } : {}),
    created_at: createdAt,
    is_incoming: input.isIncoming ?? false,
    check_number: input.checkNumber,
    transfer_batch_id: input.transferBatchId,
    auxiliary_on_us: input.checkNumber,
    sender_internal_account: input.senderInternalAccount
      ? {
          bank_account_id: input.senderInternalAccount.bankAccountId ?? '',
          account_number_id: input.senderInternalAccount.accountNumberId ?? '',
        }
      : undefined,
    receiver_internal_account: input.receiverInternalAccount
      ? {
          bank_account_id: input.receiverInternalAccount.bankAccountId ?? '',
          account_number_id: input.receiverInternalAccount.accountNumberId ?? '',
        }
      : undefined,
  } as UnifiedTransfer;
};

const isPopulatedString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const statusMatchesCacheFilters = (status: string, filters: Record<string, unknown>): boolean => {
  if (!isPopulatedString(filters.status)) return true;
  const allowed = filters.status.split(',').map((value) => value.trim().toLowerCase());
  return allowed.includes(status.toLowerCase());
};

const transferMatchesCacheFilters = (transfer: UnifiedTransfer, filters: Record<string, unknown>): boolean => {
  if (isPopulatedString(filters.starting_after) || isPopulatedString(filters.ending_before)) return false;

  if (isPopulatedString(filters.query)) return false;
  if (isPopulatedString(filters.counterparty_id)) return false;
  if (isPopulatedString(filters.account_number_id)) return false;
  if (isPopulatedString(filters.sender_account_number_id)) return false;
  if (isPopulatedString(filters.receiver_account_number_id)) return false;
  if (isPopulatedString(filters.transfer_batch_id)) return false;
  if (isPopulatedString(filters.sort_by) || isPopulatedString(filters.sort_order)) return false;

  if (typeof filters.type === 'string' && !transferTypeMatches(filters.type, transfer.type)) return false;

  if (!statusMatchesCacheFilters(transfer.status, filters)) return false;

  if (typeof filters.is_incoming === 'boolean' && filters.is_incoming !== Boolean(transfer.is_incoming)) return false;

  if (typeof filters.bank_account_id === 'string') {
    const ids = [
      transfer.sender_internal_account?.bank_account_id,
      transfer.receiver_internal_account?.bank_account_id,
    ].filter(Boolean);
    if (!ids.includes(filters.bank_account_id)) return false;
  }
  if (typeof filters.sender_bank_account_id === 'string') {
    if (transfer.sender_internal_account?.bank_account_id !== filters.sender_bank_account_id) return false;
  }
  if (typeof filters.receiver_bank_account_id === 'string') {
    if (transfer.receiver_internal_account?.bank_account_id !== filters.receiver_bank_account_id) return false;
  }

  const amountInSmallestUnit = typeof transfer.amount === 'number' ? transfer.amount : Number(transfer.amount ?? 0);
  const currency = Currencies.find(({ code }) => code === transfer.currency_code);
  const amount = currency ? (amountInSmallestUnit * 100) / 10 ** currency.decimalDigits : 0;
  const numericFilter = (key: string): number | undefined => {
    const v = filters[key];
    if (typeof v === 'number') return v;
    if (typeof v === 'string' && v.length > 0) {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  };
  const amountEq = numericFilter('amount');
  const amountGt = numericFilter('amount_gt');
  const amountGte = numericFilter('amount_gte');
  const amountLt = numericFilter('amount_lt');
  const amountLte = numericFilter('amount_lte');
  const hasAmountFilter = [amountEq, amountGt, amountGte, amountLt, amountLte].some((value) => value !== undefined);
  if (hasAmountFilter && !currency) return false;
  if (amountEq !== undefined && amount !== amountEq) return false;
  if (amountGt !== undefined && !(amount > amountGt)) return false;
  if (amountGte !== undefined && !(amount >= amountGte)) return false;
  if (amountLt !== undefined && !(amount < amountLt)) return false;
  if (amountLte !== undefined && !(amount <= amountLte)) return false;

  const createdAtMs = transfer.created_at ? new Date(transfer.created_at).getTime() : NaN;
  const dateFilter = (key: string): number | undefined => {
    const v = filters[key];
    if (typeof v !== 'string' || v.length === 0) return undefined;
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : undefined;
  };
  if (Number.isFinite(createdAtMs)) {
    const gt = dateFilter('created_at_gt');
    const gte = dateFilter('created_at_gte');
    const lt = dateFilter('created_at_lt');
    const lte = dateFilter('created_at_lte');
    if (gt !== undefined && !(createdAtMs > gt)) return false;
    if (gte !== undefined && !(createdAtMs >= gte)) return false;
    if (lt !== undefined && !(createdAtMs < lt)) return false;
    if (lte !== undefined && !(createdAtMs <= lte)) return false;
  }

  return true;
};

const addTransferToCachedList = (
  data: CachedTransferList,
  transfer: UnifiedTransfer,
  filters: Record<string, unknown>
): CachedTransferList => {
  const existingTransfers = data.transfers.filter((cachedTransfer) => cachedTransfer.id !== transfer.id);
  const isNew = existingTransfers.length === data.transfers.length;
  const totalResultsCount = isNew ? (data.total_results_count ?? data.transfers.length) + 1 : data.total_results_count;
  const limitValue = typeof filters.limit === 'string' ? Number(filters.limit) : filters.limit;
  const limit =
    typeof limitValue === 'number' && Number.isFinite(limitValue) && limitValue > 0 ? limitValue : undefined;
  const transfers = [transfer, ...existingTransfers];

  return {
    ...data,
    has_more:
      data.has_more || (isNew && limit !== undefined && totalResultsCount !== undefined && totalResultsCount > limit),
    transfers: limit === undefined ? transfers : transfers.slice(0, limit),
    total_results_count: totalResultsCount,
  };
};

export const optimisticallyAddTransfer = (
  queryClient: QueryClient,
  input: OptimisticTransferInput,
  ctx: OptimisticSessionContext
) => {
  if (typeof input.amount !== 'number' || isNaN(input.amount)) return;
  if (typeof input.status !== 'string' || input.status.length === 0) return;

  const fullTransfer = toUnifiedTransfer(input);

  const cache = queryClient.getQueryCache();
  const queries = cache.findAll({ queryKey: [{ _id: 'transferSearchTransfers' }] });

  queries.forEach((query) => {
    const expectedHash = hashKey([...query.queryKey, ctx.platformId, ctx.liveMode]);
    if (query.queryHash !== expectedHash) return;

    const data = query.state.data as CachedTransferList | undefined;
    if (!data) return;

    const keyOptions = query.queryKey[0] as { query?: Record<string, unknown> };
    const filters = keyOptions.query ?? {};

    if (!transferMatchesCacheFilters(fullTransfer, filters)) return;

    query.setData(addTransferToCachedList(data, fullTransfer, filters) as unknown);
  });
};

export const optimisticallyUpdateTransfer = (
  queryClient: QueryClient,
  input: OptimisticTransferUpdate,
  ctx: OptimisticSessionContext
) => {
  const status = input.status.toLowerCase();
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: [{ _id: 'transferSearchTransfers' }] })
    .filter((query) => query.queryHash === hashKey([...query.queryKey, ctx.platformId, ctx.liveMode]));
  const cachedTransfer = queries
    .map((query) => query.state.data as CachedTransferList | undefined)
    .flatMap((data) => data?.transfers ?? [])
    .find((transfer) => transfer.id === input.id);

  if (!cachedTransfer) return;

  const updatedTransfer = { ...cachedTransfer, status };
  if (status !== 'hold' && status !== 'pre_review') {
    delete updatedTransfer.pending_approval;
  }

  queries.forEach((query) => {
    const data = query.state.data as CachedTransferList | undefined;
    if (!data) return;

    const transferIndex = data.transfers.findIndex((transfer) => transfer.id === input.id);
    const keyOptions = query.queryKey[0] as { query?: Record<string, unknown> };
    const filters = keyOptions.query ?? {};
    const matchesStatusFilter = statusMatchesCacheFilters(status, filters);

    if (transferIndex >= 0 && !matchesStatusFilter) {
      query.setData({
        ...data,
        transfers: data.transfers.filter((transfer) => transfer.id !== input.id),
        total_results_count: Math.max((data.total_results_count ?? data.transfers.length) - 1, 0),
      } as unknown);
      return;
    }

    if (transferIndex >= 0) {
      const transfers = data.transfers.slice();
      transfers[transferIndex] = updatedTransfer;
      query.setData({ ...data, transfers } as unknown);
      return;
    }

    if (!transferMatchesCacheFilters(updatedTransfer, filters)) return;

    query.setData(addTransferToCachedList(data, updatedTransfer, filters) as unknown);
  });
};

export const useOptimisticTransferAdd = () => {
  const queryClient = useQueryClient();
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  return useCallback(
    (input: OptimisticTransferInput) =>
      optimisticallyAddTransfer(queryClient, input, { platformId, liveMode: !isSandbox }),
    [queryClient, platformId, isSandbox]
  );
};

export const useOptimisticTransferUpdate = () => {
  const queryClient = useQueryClient();
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  return useCallback(
    (input: OptimisticTransferUpdate) =>
      optimisticallyUpdateTransfer(queryClient, input, { platformId, liveMode: !isSandbox }),
    [queryClient, platformId, isSandbox]
  );
};
